import React, { FunctionComponent, useState, useEffect, useRef, ReactNode } from 'react';
import { Box, Input, List, ListItem, FormControl } from '@chakra-ui/react';
import { FormElementAutoCompleteSuggestion } from 'src/@types';
import { FormattedMessage } from 'react-intl';

interface AutocompleteProps {
  children?: ReactNode;
  suggestions: FormElementAutoCompleteSuggestion[];
  placeHolder: string;
  inputClassNames?: {
    container: string;
    input: string;
  };
  onSelect: (id: number) => void;
  showSelectedSuggestion?: boolean;
  doNotCloseAutoCompleteListWhenSearchingOnClickOutside?: boolean;
  // add prop to subscribe to in parent component
  onFiltered?: (filteredSuggestions: FormElementAutoCompleteSuggestion[]) => void;
  triggerSelect?: boolean;
}

const FormElementAutoComplete: FunctionComponent<AutocompleteProps> = ({
  children,
  suggestions,
  placeHolder,
  inputClassNames = { container: '', input: '' },
  onSelect,
  showSelectedSuggestion = false,
  doNotCloseAutoCompleteListWhenSearchingOnClickOutside = false,
  triggerSelect,
  onFiltered
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    FormElementAutoCompleteSuggestion[]
  >([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(0);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const inputValueRef = useRef<string>('');
  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (triggerSelect) handleSelect(filteredSuggestions[0]);
  }, [triggerSelect]);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      if (doNotCloseAutoCompleteListWhenSearchingOnClickOutside) {
        if (filteredSuggestions.length === 0 && inputValueRef.current === '') {
          setShowSuggestions(false);
        }
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const handleSelect = (selection: FormElementAutoCompleteSuggestion) => {
    setInputValue(showSelectedSuggestion ? selection.label : '');
    onSelect(selection.value);
    setActiveSuggestionIndex(0);
    setShowSuggestions(false);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value;

    const filtered = suggestions.filter(
      (suggestion) =>
        suggestion.label.toLowerCase().indexOf(userInput.toLowerCase()) > -1 ||
        suggestion.value.toString().indexOf(userInput.toLowerCase()) > -1
    );

    setInputValue(userInput);
    setFilteredSuggestions(filtered);
    onFiltered && onFiltered(filtered);
    setActiveSuggestionIndex(0);
    setShowSuggestions(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    //console.log(e.code);
    if (e.code === 'Enter') {
      handleSelect(filteredSuggestions[activeSuggestionIndex]);
    } else if (e.code === 'ArrowUp') {
      if (activeSuggestionIndex === 0) {
        return;
      }
      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    } else if (e.code === 'ArrowDown') {
      if (activeSuggestionIndex === filteredSuggestions.length - 1) {
        return;
      }
      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    }
  };

  return (
    <Box ref={inputRef}>
      <FormControl>
        <div className={inputClassNames.container}>
          <Input
            type="text"
            className={inputClassNames.input}
            value={inputValue}
            onChange={onChange}
            focusBorderColor={`transparent`}
            onFocus={(e) => {
              if (showSelectedSuggestion) {
                e.target.select();
              }
            }}
            onKeyDown={onKeyDown}
            placeholder={placeHolder}
          />
          {children}
        </div>
      </FormControl>
      {showSuggestions && inputValue && (
        <List
          border="1px solid #ccc"
          borderRadius="md"
          mt={2}
          p={2}
          width="100%"
          zIndex="popover"
          bg="white"
          position="absolute">
          {filteredSuggestions.length === 0 && (
            <ListItem p={2} color="gray.500">
              <FormattedMessage
                id={`shared.form.elements.auto-complete.no-suggestions`}
                defaultMessage={`Geen resultaat gevonden`}
              />
            </ListItem>
          )}
          {filteredSuggestions.map((suggestion, index) => (
            <ListItem
              key={suggestion.value}
              bg={index === activeSuggestionIndex ? '#ddd' : '#fff'}
              p={2}
              cursor="pointer"
              onClick={() => handleSelect(suggestion)}>
              {suggestion.label}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default FormElementAutoComplete;
